import { useState, useEffect } from 'react';
import { Bell, Save, Send, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { userApi } from '@/services/api';
import { getBarkConfig, saveBarkConfig, testBarkConfig } from '@/services/barkNotification';

export function PushSettingsPage() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  // 提醒偏好
  const [enabled, setEnabled] = useState(true);
  const [delayMinutes, setDelayMinutes] = useState(60);

  // Bark 配置
  const [barkKey, setBarkKey] = useState('');
  const [barkKeyMask, setBarkKeyMask] = useState('');
  const [barkServerUrl, setBarkServerUrl] = useState('');

  // 最近一次验证结果：'success' | 'error' | null
  const [verifyStatus, setVerifyStatus] = useState<'success' | 'error' | null>(null);
  const [verifyMessage, setVerifyMessage] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    setVerifyStatus(null);
    try {
      const [reminderRes, barkRes] = await Promise.all([
        userApi.getReminderSettings(),
        getBarkConfig(),
      ]);
      const reminderData = reminderRes.data.data;
      setEnabled(reminderData.landingReminderEnabled);
      setDelayMinutes(reminderData.remindAfterLandingMinutes);

      setBarkKeyMask(barkRes.barkKeyMask);
      setBarkServerUrl(barkRes.barkServerUrl);
    } catch {
      toast({ title: '获取设置失败', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveReminderSettings = async () => {
    setSaving(true);
    try {
      await userApi.updateReminderSettings({
        landingReminderEnabled: enabled,
        remindAfterLandingMinutes: delayMinutes,
      });
      toast({ title: '提醒偏好已保存' });
    } catch {
      toast({ title: '保存失败', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBarkConfig = async () => {
    const trimmedKey = barkKey.trim();
    if (!trimmedKey) {
      toast({ title: '请输入 Bark Device Key', variant: 'destructive' });
      return;
    }

    setSaving(true);
    setVerifyStatus(null);
    try {
      const res = await saveBarkConfig({
        barkKey: trimmedKey,
        barkServerUrl: barkServerUrl || undefined,
      });
      const data = res.data.data;
      setBarkKey('');
      if (data) {
        setBarkKeyMask(data.barkKeyMask);
        setBarkServerUrl(data.barkServerUrl);
      }
      setVerifyStatus('success');
      setVerifyMessage(res.data.message || 'Bark 配置已保存并验证通过');
      toast({ title: res.data.message || 'Bark 配置已保存并验证通过' });
    } catch (err: any) {
      const message = err?.response?.data?.message || '验证失败，配置未更新';
      setVerifyStatus('error');
      setVerifyMessage(message);
      toast({ title: message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const runBarkTest = async () => {
    if (!barkKeyMask) {
      toast({ title: '请先配置 Bark Device Key', variant: 'destructive' });
      return;
    }

    setTesting(true);
    setVerifyStatus(null);
    try {
      const result = await testBarkConfig();
      if (result.success) {
        setVerifyStatus('success');
        setVerifyMessage(result.message || '测试推送已发送，请检查手机');
        toast({ title: result.message || '测试推送已发送，请检查手机' });
      } else {
        setVerifyStatus('error');
        setVerifyMessage(result.message || '测试失败');
        toast({ title: result.message || '测试失败', variant: 'destructive' });
      }
    } catch (err: any) {
      const message = err?.response?.data?.message || err.message || '测试失败';
      setVerifyStatus('error');
      setVerifyMessage(message);
      toast({ title: message, variant: 'destructive' });
    } finally {
      setTesting(false);
    }
  };

  const handleTestBark = () => runBarkTest();

  const hasBarkKey = Boolean(barkKeyMask);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Bark 推送设置</h1>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Bark 配置
          </CardTitle>
          <CardDescription>配置 Bark Device Key 以接收航班落地提醒</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {hasBarkKey && (
            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-md">
              <CheckCircle2 className="w-4 h-4" />
              <span>已配置 Bark Device Key：{barkKeyMask}</span>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="bark-key">Bark Device Key</Label>
            <Input
              id="bark-key"
              type="text"
              placeholder={
                hasBarkKey
                  ? '输入新 Key 可更换当前配置，留空保持不变'
                  : '请输入 Bark Device Key'
              }
              value={barkKey}
              onChange={(e) => setBarkKey(e.target.value)}
            />
            <p className="text-xs text-gray-500">
              {hasBarkKey
                ? '当前已配置 Bark Key，输入新值可更换，留空则保持不变'
                : '在 Bark App 中复制 Device Key 并粘贴到这里'}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="bark-server">Bark 服务器地址（可选）</Label>
            <Input
              id="bark-server"
              type="url"
              placeholder="https://api.day.app"
              value={barkServerUrl}
              onChange={(e) => setBarkServerUrl(e.target.value)}
            />
            <p className="text-xs text-gray-500">留空则使用系统默认 Bark 服务器</p>
          </div>

          {verifyStatus && (
            <div
              className={`flex items-center gap-2 text-sm px-3 py-2 rounded-md ${
                verifyStatus === 'success'
                  ? 'text-green-700 bg-green-50'
                  : 'text-red-700 bg-red-50'
              }`}
            >
              {verifyStatus === 'success' ? (
                <CheckCircle2 className="w-4 h-4" />
              ) : (
                <XCircle className="w-4 h-4" />
              )}
              <span>{verifyMessage}</span>
            </div>
          )}

          <div className="flex gap-3">
            <Button onClick={handleSaveBarkConfig} disabled={saving} className="gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              保存并验证
            </Button>
            <Button variant="outline" onClick={handleTestBark} disabled={testing || !hasBarkKey} className="gap-2">
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              发送测试
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>提醒偏好</CardTitle>
          <CardDescription>自定义航班落地后的提醒时机</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="landing-reminder">落地后提醒</Label>
              <p className="text-xs text-gray-500">航班计划落地后发送笔记记录提醒</p>
            </div>
            <Switch id="landing-reminder" checked={enabled} onCheckedChange={setEnabled} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="delay-minutes">提醒延迟（分钟）</Label>
            <Input
              id="delay-minutes"
              type="number"
              min={0}
              max={720}
              value={delayMinutes}
              onChange={(e) => setDelayMinutes(Number(e.target.value))}
            />
            <p className="text-xs text-gray-500">航班计划落地后多少分钟发送提醒</p>
          </div>

          <Button onClick={handleSaveReminderSettings} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            保存提醒偏好
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
